// src/pages/GenerarReciboCobro.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileInvoiceDollar, FaUpload, FaPrint, FaChevronLeft, FaSearch, FaEnvelope, FaHistory, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaTimes, FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaExpand } from 'react-icons/fa';
import { db, storage, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { collection, query, onSnapshot, orderBy, doc, getDoc, updateDoc, Timestamp, where, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { calcularEstadoMoroso } from '../utils/morosoUtils';
import { pushToSyncQueue } from '../utils/offlineSyncQueue';
import './GenerarReciboCobro.css';

const DEFAULT_REDACCION = `Estimado/a C. {residente} Unidad Privativa: {numeroCasa}, se le informa que se ha generado una nueva cuota.`;

const GenerarReciboCobro = () => {
    console.log('GenerarReciboCobro component rendering');
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    console.log('fraccionamientoId:', fraccionamientoId);
    const [casas, setCasas] = useState([]);
    const [selectedCasa, setSelectedCasa] = useState(null);
    const [selectedCasas, setSelectedCasas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fraccionamientoData, setFraccionamientoData] = useState(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [firmaUrl, setFirmaUrl] = useState('');
    const [direccionFraccionamiento, setDireccionFraccionamiento] = useState('');
    const [ciudadEstado, setCiudadEstado] = useState('San Juan del Río Querétaro');
    const [uploadingFirma, setUploadingFirma] = useState(false);

    // Fechas en lote
    const [fechaComienzoAdeudo, setFechaComienzoAdeudo] = useState(new Date());
    const [fechaFinalAdeudo, setFechaFinalAdeudo] = useState(new Date());

    const [tituloRecibo, setTituloRecibo] = useState('Cuota de Mantenimiento');
    const [redaccionRecibo, setRedaccionRecibo] = useState('');
    const [configFinanciera, setConfigFinanciera] = useState({
        cuotaMensual: 0,
        corteDia: 1,
        sancionesInicioDia: 1
    });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [historialVisible, setHistorialVisible] = useState(false);
    const [historialRecibos, setHistorialRecibos] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [historialPage, setHistorialPage] = useState(1);
    const [modalMessage, setModalMessage] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    // Editor visual
    const [editorModalOpen, setEditorModalOpen] = useState(false);
    const [fontSize, setFontSize] = useState(12);
    const [fontFamily, setFontFamily] = useState('Times New Roman');
    const editorRef = useRef(null);

    // Cargar datos del fraccionamiento
    useEffect(() => {
        const loadFraccionamientoData = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};

                    // Obtener logo
                    const logo = configRecibos.logoUrl || data.logoUrl || '';
                    setLogoUrl(logo);

                    // Obtener firma guardada
                    const firma = configRecibos.firmaReciboCobroUrl || configRecibos.firmaConstanciaUrl || '';
                    setFirmaUrl(firma);

                    // Obtener nombre del fraccionamiento
                    const nombre = configRecibos.condominioNombre || data.condominioNombre || data.nombre || '';

                    // Obtener dirección
                    const direccion = configRecibos.condominioDireccion || data.condominioDireccion || data.direccion || '';
                    setDireccionFraccionamiento(direccion);

                    // Cargar configuración financiera
                    const configFin = data.configuracionFinanciera || {};
                    setConfigFinanciera({
                        cuotaMensual: configFin.montoMensual || configFin.cuotaMensual || 0,
                        corteDia: configFin.corteDia || 1,
                        sancionesInicioDia: configFin.sancionesInicioDia || configFin.corteDia || 1
                    });

                    // Cargar configuración de recibo guardada
                    const configRecibo = configRecibos.configuracionReciboCobro || {};
                    if (configRecibo.titulo) setTituloRecibo(configRecibo.titulo);
                    if (configRecibo.redaccion) setRedaccionRecibo(configRecibo.redaccion);
                    if (configRecibo.fechaComienzo) {
                        const fechaGuardadaC = configRecibo.fechaComienzo.toDate ? configRecibo.fechaComienzo.toDate() : new Date(configRecibo.fechaComienzo);
                        setFechaComienzoAdeudo(fechaGuardadaC);
                    }
                    if (configRecibo.fechaFinal) {
                        const fechaGuardadaF = configRecibo.fechaFinal.toDate ? configRecibo.fechaFinal.toDate() : new Date(configRecibo.fechaFinal);
                        setFechaFinalAdeudo(fechaGuardadaF);
                    }
                    if (configRecibo.fontSize) setFontSize(configRecibo.fontSize);
                    if (configRecibo.fontFamily) setFontFamily(configRecibo.fontFamily);

                    setFraccionamientoData({
                        nombre,
                        direccion,
                        adminNombre: data.adminNombre || data.administradorNombre || ''
                    });
                }
            } catch (error) {
                console.error('Error cargando datos del fraccionamiento:', error);
            }
        };

        loadFraccionamientoData();
    }, [fraccionamientoId]);

    // Cargar solo casas morosas (nueva regla: adeudo >= umbral)
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(casasRef, orderBy('numero'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const casasList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar solo casas morosas usando nueva lógica de umbral
            const casasMorosas = casasList.filter(casa => {
                const adeudo = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                const cuotaMensual = Number(configFinanciera.cuotaMensual || 0);
                const mesesPermitidos = Number(configFinanciera.mesesPermitidos || 1);
                return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
            });

            setCasas(casasMorosas);
            setLoading(false);
        }, (error) => {
            console.error('Error cargando casas:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, configFinanciera]);

    const getResidentName = (casa) => {
        return casa?.nombre_residente ||
            casa?.residente ||
            casa?.usuario ||
            casa?.nombreResidente ||
            (casa?.propietario && casa.propietario.nombre) ||
            (casa?.contacto && casa.contacto.nombre) ||
            'Residente no registrado';
    };

    const filteredCasas = casas.filter(casa => {
        const searchLower = searchTerm.toLowerCase();
        const numero = String(casa.numero || '').toLowerCase();
        const nombre = getResidentName(casa).toLowerCase();
        return numero.includes(searchLower) || nombre.includes(searchLower);
    });

    const handleCasaSelect = (casa) => {
        setSelectedCasa(casa);
        // También agregar/quitar de la lista de seleccionados
        setSelectedCasas(prev => {
            const exists = prev.find(c => c.id === casa.id);
            if (exists) {
                return prev.filter(c => c.id !== casa.id);
            } else {
                return [...prev, casa];
            }
        });
    };

    const handleSelectAll = () => {
        if (filteredCasas.length === 0) return;

        // Si ya están todos seleccionados, deseleccionar todos
        const allSelected = filteredCasas.every(casa =>
            selectedCasas.find(c => c.id === casa.id)
        );

        if (allSelected) {
            setSelectedCasas([]);
            setSelectedCasa(null);
        } else {
            setSelectedCasas([...filteredCasas]);
            // Seleccionar la primera casa para mostrar el preview
            setSelectedCasa(filteredCasas[0]);
        }
    };

    const handleFirmaUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !fraccionamientoId) return;

        setUploadingFirma(true);
        try {
            const fileRef = ref(storage, `fraccionamientos/${fraccionamientoId}/recibos/firma_${Date.now()}.${file.name.split('.').pop()}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            setFirmaUrl(url);

            // Guardar URL en Firestore
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    firmaReciboCobroUrl: url
                }
            });
        } catch (error) {
            console.error('Error subiendo firma:', error);
            alert('Error al subir la firma. Por favor intenta nuevamente.');
        } finally {
            setUploadingFirma(false);
        }
    };

    const formatDate = (date, ciudadEstado) => {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const day = date.getDate().toString().padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${ciudadEstado} a ${day} de ${month} de ${year}.`;
    };

    const guardarConfiguracionRecibo = async () => {
        if (!fraccionamientoId) return;
        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    configuracionReciboCobro: {
                        titulo: tituloRecibo,
                        redaccion: redaccionRecibo,
                        fechaComienzo: Timestamp.fromDate(fechaComienzoAdeudo),
                        fechaFinal: Timestamp.fromDate(fechaFinalAdeudo),
                        fontSize,
                        fontFamily
                    }
                }
            });
        } catch (error) {
            console.error('Error guardando configuración de recibo:', error);
        }
    };

    const resolveTemplate = (template, replaceData) => {
        if (!template) return '';
        let resolved = template;
        for (const [key, value] of Object.entries(replaceData)) {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            resolved = resolved.replace(regex, value);
        }
        return resolved;
    };

    const generarRecibosEnLote = (casa = null) => {
        const casaToUse = casa || selectedCasa;
        if (!casaToUse || !fraccionamientoData) return [];

        const residenteNombre = getResidentName(casaToUse);
        const numeroCasa = casaToUse.numero || 'N/A';
        const nombreFracc = fraccionamientoData.nombre || 'N/A';
        const direccion = direccionFraccionamiento || 'N/A';
        const adminNombreStr = fraccionamientoData.adminNombre || 'Administrador(a)';
        const cuotaMensual = configFinanciera.cuotaMensual || 0;

        const mesesDict = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        let recibos = [];
        let currentDate = new Date(fechaComienzoAdeudo.getFullYear(), fechaComienzoAdeudo.getMonth(), 1);
        const endDateObj = new Date(fechaFinalAdeudo.getFullYear(), fechaFinalAdeudo.getMonth(), 1);

        // Seguridad: si el inicio es posterior al final, solo cobramos el mes de inicio
        if (currentDate > endDateObj) {
            currentDate = endDateObj;
        }

        while (currentDate <= endDateObj) {
            const currentMonth = currentDate.getMonth();
            const currentYear = currentDate.getFullYear();
            const mesNombreStr = mesesDict[currentMonth];

            // Calcular fecha de la deuda (día de corte del mes iterado)
            const diaCorte = configFinanciera.corteDia || 1;
            const fechaDeuda = new Date(currentYear, currentMonth, diaCorte);
            const fechaDeudaFormato = `${String(fechaDeuda.getDate()).padStart(2, '0')}-${String(fechaDeuda.getMonth() + 1).padStart(2, '0')}-${fechaDeuda.getFullYear()}`;

            // Calcular fecha de vencimiento (día de inicio de sanciones)
            const diaSanciones = configFinanciera.sancionesInicioDia || diaCorte;
            const fechaVencimiento = new Date(currentYear, currentMonth, diaSanciones);
            const fechaVencimientoFormato = `${String(fechaVencimiento.getDate()).padStart(2, '0')}-${String(fechaVencimiento.getMonth() + 1).padStart(2, '0')}-${fechaVencimiento.getFullYear()}`;

            // Redacción interactiva
            const replaceData = {
                nombreFracc,
                admin: adminNombreStr,
                numeroCasa,
                residente: residenteNombre,
                direccion,
                adeudo: cuotaMensual,
                adeudoFormato: `$${cuotaMensual.toFixed(2)}`
            };

            const textoCuerpo = resolveTemplate(redaccionRecibo || DEFAULT_REDACCION, replaceData);

            recibos.push({
                residente: residenteNombre,
                numeroCasa,
                nombreFracc,
                direccion,
                titulo: tituloRecibo,
                textoCuerpo, // Ahora podría contener HTML
                importe: cuotaMensual,
                concepto: `Mantenimiento ${mesNombreStr} ${currentYear}`,
                fechaDeuda: fechaDeudaFormato,
                fechaVencimiento: fechaVencimientoFormato
            });

            // Avanzar 1 mes
            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        return recibos;
    };

    const sendReciboCobro = async (casa = null) => {
        const casaToUse = casa || selectedCasa;
        if (!casaToUse || !fraccionamientoData) {
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'Por favor selecciona un residente moroso primero.'
            });
            return;
        }

        const recibos = generarRecibosEnLote(casaToUse);
        if (!recibos || recibos.length === 0) {
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'No se pudo generar el recibo.'
            });
            return;
        }

        // Obtener email del residente
        const email = casaToUse.email || casaToUse.correo || casaToUse.correoElectronico || '';
        if (!email) {
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'No hay correo registrado para este residente.'
            });
            return;
        }

        setSendingEmail(true);

        if (!navigator.onLine) {
            pushToSyncQueue('sendReciboCobro', {
                email,
                recibosData: recibos,
                casaInfo: {
                    id: casaToUse.id,
                    numero: casaToUse.numero,
                    fraccionamientoId: fraccionamientoId
                },
                fraccId: fraccionamientoId
            });
            setModalMessage({
                type: 'success',
                title: 'Modo Offline',
                message: `El recibo para ${email} se guardó y se enviará automáticamente cuando recuperes la conexión.`
            });
            setSendingEmail(false);
            return;
        }

        try {
            const sendReciboCobroFn = httpsCallable(functions, 'sendReciboCobro');
            const response = await sendReciboCobroFn({
                email,
                recibosData: recibos,
                casaInfo: {
                    id: casaToUse.id,
                    numero: casaToUse.numero,
                    fraccionamientoId: fraccionamientoId
                },
                fraccId: fraccionamientoId
            });

            if (response.data.success) {
                setModalMessage({
                    type: 'success',
                    title: '¡Éxito!',
                    message: `Recibo de cobro enviado exitosamente a ${email}`
                });
            } else {
                setModalMessage({
                    type: 'error',
                    title: 'Error al enviar',
                    message: response.data.message || 'Error al enviar correo'
                });
            }
        } catch (error) {
            console.error('Error enviando recibo de cobro:', error);
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'Error al enviar el correo. Por favor intenta nuevamente.'
            });
        } finally {
            setSendingEmail(false);
        }
    };

    const sendAllRecibosCobro = async () => {
        if (selectedCasas.length === 0) {
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'Por favor selecciona al menos un residente moroso.'
            });
            return;
        }

        // Mostrar modal de confirmación personalizado
        setConfirmModal({
            title: 'Confirmar envío de recibos',
            message: `¿Estás seguro de enviar ${selectedCasas.length} recibos de cobro?`,
            details: `Los correos se enviarán a todas las casas seleccionadas. El sistema procesará automáticamente el envío de Recibos de Cobro.`,
            onConfirm: async () => {
                setConfirmModal(null);
                await executeSendAllRecibosCobro();
            },
            onCancel: () => {
                setConfirmModal(null);
            }
        });
    };

    const executeSendAllRecibosCobro = async () => {

        setSendingEmail(true);

        try {
            // Preparar datos de las casas para la cola
            const casasParaCola = selectedCasas.map(casa => {
                const recibos = generarRecibosEnLote(casa);
                if (!recibos || recibos.length === 0) return null;

                const email = casa.email || casa.correo || casa.correoElectronico || '';
                if (!email) return null;

                return {
                    id: casa.id,
                    numero: casa.numero,
                    email: email,
                    recibosData: recibos,
                    casaInfo: {
                        id: casa.id,
                        numero: casa.numero,
                        fraccionamientoId: fraccionamientoId
                    }
                };
            }).filter(casa => casa !== null);

            if (casasParaCola.length === 0) {
                setModalMessage({
                    type: 'error',
                    title: 'Error',
                    message: 'No hay casas válidas con correo electrónico para enviar.'
                });
                setSendingEmail(false);
                return;
            }

            if (!navigator.onLine) {
                pushToSyncQueue('createReciboCobroQueue', {
                    fraccionamientoId: fraccionamientoId,
                    casas: casasParaCola
                });
                setModalMessage({
                    type: 'success',
                    title: 'Modo Offline',
                    message: `Se programó el envío para ${casasParaCola.length} casas automáticamente cuando recuperes la conexión.`
                });
                setSendingEmail(false);
                return;
            }

            // Crear cola de envíos
            const createQueueFn = httpsCallable(functions, 'createReciboCobroQueue');
            const queueResponse = await createQueueFn({
                fraccionamientoId: fraccionamientoId,
                casas: casasParaCola
            });

            if (queueResponse.data.success) {
                // Iniciar procesamiento del primer lote
                const processQueueFn = httpsCallable(functions, 'processReciboCobroQueue');
                await processQueueFn({
                    fraccionamientoId: fraccionamientoId,
                    queueId: queueResponse.data.queueId,
                    batchSize: 60
                });

                setModalMessage({
                    type: 'success',
                    title: '¡Correo enviado con éxito!',
                    message: `Se creó una cola con ${casasParaCola.length} recibos de cobro. ` +
                        `Los correos se enviarán a todas las casas con adeudo.` +
                        `El sistema enviará automáticamente los Recibos de Cobro a las casas seleccionadas.`
                });
            } else {
                setModalMessage({
                    type: 'error',
                    title: 'Error al crear cola',
                    message: queueResponse.data.message || 'Error desconocido al crear la cola de envíos.'
                });
            }
        } catch (error) {
            console.error('Error creando cola de recibos:', error);

            // Determinar el tipo de error y mensaje específico
            let errorMessage = 'Error al crear la cola de envíos. Por favor intenta nuevamente.';
            let errorTitle = 'Error';

            if (error.code === 'functions/not-found' || error.code === 'functions/not-found') {
                errorTitle = 'Función no encontrada';
                errorMessage = 'La función de envío masivo no está disponible. Por favor, contacta al administrador o verifica que las funciones estén desplegadas.';
            } else if (error.code === 'functions/unauthenticated') {
                errorTitle = 'No autenticado';
                errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
            } else if (error.code === 'functions/permission-denied') {
                errorTitle = 'Sin permisos';
                errorMessage = 'No tienes permisos para realizar esta acción.';
            } else if (error.message && error.message.includes('404')) {
                errorTitle = 'Función no desplegada';
                errorMessage = 'La función Cloud Function no está desplegada. Por favor, contacta al administrador para desplegar las funciones.';
            } else if (error.message && error.message.includes('CORS')) {
                errorTitle = 'Error de conexión';
                errorMessage = 'Error de conexión con el servidor. Por favor, verifica tu conexión a internet e intenta nuevamente.';
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }

            setModalMessage({
                type: 'error',
                title: errorTitle,
                message: errorMessage
            });
        } finally {
            setSendingEmail(false);
        }
    };

    useEffect(() => {
        if (!historialVisible || !fraccionamientoId) {
            setHistorialRecibos([]);
            setLoadingHistorial(false);
            return;
        }

        setLoadingHistorial(true);
        try {
            const historialRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historialRecibosCobro');
            const q = query(historialRef, orderBy('enviadoEn', 'desc'), limit(50));
            const unsubscribe = onSnapshot(q,
                (snapshot) => {
                    const historial = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setHistorialRecibos(historial);
                    setLoadingHistorial(false);
                },
                (error) => {
                    console.error('Error cargando historial:', error);
                    setLoadingHistorial(false);
                    setModalMessage({
                        type: 'error',
                        title: 'Error',
                        message: 'No se pudo cargar el historial. Verifica los permisos de Firestore.'
                    });
                }
            );
            return () => unsubscribe();
        } catch (error) {
            console.error('Error cargando historial:', error);
            setLoadingHistorial(false);
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'Error al cargar el historial. Por favor intenta nuevamente.'
            });
        }
    }, [historialVisible, fraccionamientoId]);

    const handlePrint = (casa = null) => {
        const recibos = generarRecibosEnLote(casa);
        if (!recibos || recibos.length === 0) {
            setModalMessage({
                type: 'error',
                title: 'Error',
                message: 'Por favor selecciona un residente moroso primero.'
            });
            return;
        }

        const printWindow = window.open('', '_blank');

        const recibosHtml = recibos.map((recibo, index) => `
            <div class="recibo-page-break" ${index < recibos.length - 1 ? 'style="page-break-after: always;"' : ''}>
                <div class="logo-container">
                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo" />` : ''}
                    <div class="nombre-fraccionamiento">${recibo.nombreFracc}</div>
                </div>
                
                <div class="titulo">${recibo.titulo}</div>
                
                <div class="cuerpo" style="font-family: '${fontFamily}'; font-size: ${fontSize}pt;">
                    <div class="redaccion-content">${recibo.textoCuerpo}</div>
                    
                    <div class="detalle-linea">
                        Importe: <strong>$${recibo.importe.toFixed(2)}</strong>
                    </div>
                    <div class="detalle-linea">
                        Concepto: <strong>${recibo.concepto}</strong>
                    </div>
                    <div class="detalle-linea">
                        Fecha de la deuda: <strong>${recibo.fechaDeuda}</strong>
                    </div>
                    <div class="detalle-linea">
                        Fecha de vencimiento: <strong>${recibo.fechaVencimiento}</strong>
                    </div>
                </div>
                
                <div class="direccion-footer">
                    ${recibo.direccion}
                </div>
            </div>
        `).join('');

        const htmlContent = `
            <!DOCTYPE html>
            <html lang="es">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Cuota de Mantenimiento</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body {
                        font-family: 'Times New Roman', serif;
                        font-size: 12pt;
                        line-height: 1.6;
                        color: #000;
                        padding: 40px;
                        background: white;
                    }
                    .logo-container {
                        text-align: center;
                        margin-bottom: 20px;
                    }
                    .logo {
                        max-width: 200px;
                        max-height: 100px;
                        object-fit: contain;
                        margin: 0 auto;
                        display: block;
                    }
                    .nombre-fraccionamiento {
                        text-align: center;
                        font-size: 11pt;
                        margin-top: 10px;
                        margin-bottom: 20px;
                    }
                    .titulo {
                        text-align: center;
                        font-size: 16pt;
                        font-weight: bold;
                        margin: 20px 0;
                        text-transform: uppercase;
                    }
                    .cuerpo {
                        text-align: left;
                        margin: 20px 0;
                        line-height: 1.8;
                    }
                    .detalle-linea {
                        margin: 10px 0;
                        text-align: left;
                    }
                    .detalle-linea strong {
                        font-weight: bold;
                    }
                    .direccion-footer {
                        text-align: center;
                        margin-top: 40px;
                        font-size: 11pt;
                    }
                    @media print {
                        body { padding: 20px; }
                        .recibo-page-break { page-break-after: always; }
                    }
                </style>
            </head>
            <body>
                ${recibosHtml}
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    // Si no hay fraccionamientoId, mostrar mensaje
    if (!fraccionamientoId) {
        return (
            <div className="recibo-page">
                <div className="recibo-header-wrapper">
                    <header className="recibo-header">
                        <div className="recibo-header-icon">
                            <FaFileInvoiceDollar />
                        </div>
                        <div>
                            <h2>Generar Recibo de Cobro</h2>
                            <p>No se encontró información del fraccionamiento</p>
                        </div>
                        <button onClick={() => navigate('/administracion')} className="recibo-back-link">
                            <FaChevronLeft /> Regresar a administración
                        </button>
                    </header>
                </div>
            </div>
        );
    }


    return (
        <div className="recibo-page">
            <div className="recibo-header-wrapper">
                <header className="recibo-header">
                    <div className="recibo-header-icon">
                        <FaFileInvoiceDollar />
                    </div>
                    <div>
                        <h2>Generar Recibo de Cobro</h2>
                        <p>Genera recibos de cobro para residentes morosos</p>
                    </div>
                    <button onClick={() => navigate('/administracion')} className="recibo-back-link">
                        <FaChevronLeft /> Regresar a administración
                    </button>
                </header>
            </div>

            <div className="recibo-content">
                <div className="recibo-top-section">
                    <div className="recibo-card">
                        <h3>Seleccionar Residente Moroso</h3>
                        <div className="search-bar">
                            <div className="search-input-wrapper">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por número de casa o nombre del residente..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            {!loading && filteredCasas.length > 0 && (
                                <button
                                    type="button"
                                    className="select-all-button btn-premium-outline"
                                    onClick={handleSelectAll}
                                >
                                    {filteredCasas.every(casa =>
                                        selectedCasas.find(c => c.id === casa.id)
                                    ) ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                                </button>
                            )}
                        </div>

                        {loading ? (
                            <div className="loading-message">Cargando residentes morosos...</div>
                        ) : filteredCasas.length === 0 ? (
                            <div className="empty-message">
                                {searchTerm ? 'No se encontraron residentes morosos con ese criterio' : 'No hay residentes morosos'}
                            </div>
                        ) : (
                            <div className="casas-list">
                                {filteredCasas.map(casa => {
                                    const adeudo = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                                    return (
                                        <div
                                            key={casa.id}
                                            className={`casa-item ${selectedCasa?.id === casa.id ? 'selected' : ''} ${selectedCasas.find(c => c.id === casa.id) ? 'multi-selected' : ''}`}
                                            onClick={() => handleCasaSelect(casa)}
                                        >
                                            <div className="casa-info">
                                                <span className="casa-numero">Casa {casa.numero}</span>
                                                <span className="casa-residente">{getResidentName(casa)}</span>
                                                <span className="casa-adeudo">Adeudo: ${adeudo.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="recibo-card">
                        <h3>Configuración del Documento</h3>

                        <div className="form-group-row dates-row">
                            <div className="form-group">
                                <label>Fecha Comienzo Adeudo</label>
                                <div className="premium-date-wrapper">
                                    <input
                                        type="date"
                                        className="premium-date-input"
                                        max={new Date().toISOString().split('T')[0]}
                                        value={fechaComienzoAdeudo.toISOString().split('T')[0]}
                                        onClick={(e) => {
                                            if (e.target.showPicker) e.target.showPicker();
                                        }}
                                        onChange={(e) => {
                                            setFechaComienzoAdeudo(new Date(e.target.value));
                                            guardarConfiguracionRecibo();
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Fecha Final Adeudo</label>
                                <div className="premium-date-wrapper">
                                    <input
                                        type="date"
                                        className="premium-date-input"
                                        max={new Date().toISOString().split('T')[0]}
                                        value={fechaFinalAdeudo.toISOString().split('T')[0]}
                                        onClick={(e) => {
                                            if (e.target.showPicker) e.target.showPicker();
                                        }}
                                        onChange={(e) => {
                                            setFechaFinalAdeudo(new Date(e.target.value));
                                            guardarConfiguracionRecibo();
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Título del Documento</label>
                            <input
                                type="text"
                                value={tituloRecibo}
                                onChange={(e) => {
                                    setTituloRecibo(e.target.value);
                                    guardarConfiguracionRecibo();
                                }}
                                placeholder="Ej: RECIBO DE COBRO"
                            />
                        </div>

                        <div className="form-group">
                            <label className="label-with-tooltip">
                                Redacción del Documento
                                <div className="tooltip-container">
                                    <FaExclamationTriangle className="info-icon" />
                                    <span className="tooltip-text">
                                        Visualización de la redacción actual. Para modificarla, use el botón "Abrir Editor".
                                    </span>
                                </div>
                            </label>
                            <div
                                className="redaccion-preview-box"
                                dangerouslySetInnerHTML={{ __html: redaccionRecibo || DEFAULT_REDACCION }}
                            />

                            <button
                                type="button"
                                className="open-editor-btn btn-premium-solid"
                                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}
                                onClick={() => setEditorModalOpen(true)}
                            >
                                <FaExpand /> Abrir Editor
                            </button>
                        </div>
                    </div>
                </div>

                <div className="recibo-preview-section">
                    <div className="recibo-card preview-card">
                        <div className="preview-header">
                            <h3>Vista Previa {selectedCasas.length > 0 && `(${selectedCasas.length} seleccionado${selectedCasas.length > 1 ? 's' : ''})`}</h3>
                            <div className="preview-header-actions">
                                {selectedCasas.length > 0 && (
                                    <button
                                        className="send-all-button"
                                        onClick={sendAllRecibosCobro}
                                        disabled={sendingEmail}
                                    >
                                        <FaEnvelope /> {sendingEmail ? 'Enviando...' : `Enviar a Todos (${selectedCasas.length})`}
                                    </button>
                                )}
                                <button
                                    className="history-button"
                                    onClick={() => {
                                        setHistorialVisible(!historialVisible);
                                        if (!historialVisible) setHistorialPage(1);
                                    }}
                                >
                                    <FaHistory /> Historial
                                </button>
                            </div>
                        </div>

                        {selectedCasas.length > 0 ? (
                            <div className="previews-container">
                                {selectedCasas.map(casa => {
                                    const recibos = generarRecibosEnLote(casa);
                                    const recibo = recibos && recibos.length > 0 ? recibos[0] : null;
                                    if (!recibo) return null;

                                    return (
                                        <div key={casa.id} className="preview-item">
                                            <div className="preview-item-header">
                                                <div className="preview-casa-label-wrapper">
                                                    <span className="preview-casa-label">Casa {casa.numero} - {getResidentName(casa)}</span>
                                                    {recibos.length > 1 && (
                                                        <span className="badge-hojas">{recibos.length} hojas calculadas</span>
                                                    )}
                                                </div>
                                                <div className="preview-item-actions">
                                                    <button
                                                        className="send-button-small"
                                                        onClick={() => sendReciboCobro(casa)}
                                                        disabled={sendingEmail}
                                                        title="Enviar por correo"
                                                    >
                                                        <FaEnvelope /> Enviar
                                                    </button>
                                                    <button
                                                        className="print-button-small"
                                                        onClick={() => handlePrint(casa)}
                                                        title="Imprimir este recibo"
                                                    >
                                                        <FaPrint /> Imprimir
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="preview-content">
                                                <div className="preview-document">
                                                    <div className="preview-logo-container">
                                                        {logoUrl && (
                                                            <img src={logoUrl} alt="Logo" className="preview-logo" />
                                                        )}
                                                        <div className="preview-nombre-fraccionamiento">{recibo.nombreFracc}</div>
                                                    </div>

                                                    <div className="preview-titulo">{recibo.titulo}</div>

                                                    <div className="preview-cuerpo">
                                                        <p>{recibo.textoCuerpo}</p>

                                                        <div className="preview-detalle-linea">
                                                            Importe: <strong>${recibo.importe.toFixed(2)}</strong>
                                                        </div>
                                                        <div className="preview-detalle-linea">
                                                            Concepto: <strong>{recibo.concepto}</strong>
                                                        </div>
                                                        <div className="preview-detalle-linea">
                                                            Fecha de la deuda: <strong>{recibo.fechaDeuda}</strong>
                                                        </div>
                                                        <div className="preview-detalle-linea">
                                                            Fecha de vencimiento: <strong>{recibo.fechaVencimiento}</strong>
                                                        </div>
                                                    </div>

                                                    <div className="preview-direccion-footer">
                                                        {recibo.direccion}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="preview-empty">
                                <p>Selecciona uno o más residentes morosos para ver la vista previa</p>
                            </div>
                        )}
                    </div>
                </div>

                {historialVisible && (
                    <div className="recibo-card historial-card">
                        <div className="historial-header">
                            <h3>Historial de Recibos Enviados</h3>
                            <button
                                className="close-historial-button"
                                onClick={() => setHistorialVisible(false)}
                            >
                                ✕
                            </button>
                        </div>
                        {loadingHistorial ? (
                            <div className="loading-message">Cargando historial...</div>
                        ) : historialRecibos.length === 0 ? (
                            <div className="empty-message">No hay recibos enviados aún</div>
                        ) : (
                            <>
                                <div className="historial-list">
                                    {historialRecibos.slice((historialPage - 1) * 12, historialPage * 12).map(item => (
                                        <div key={item.id} className="historial-item">
                                            <div className="historial-item-info">
                                                <div className="historial-item-header">
                                                    <strong>Casa {item.numeroCasa}</strong>
                                                    <span className={`historial-status ${item.exito ? 'success' : 'error'}`}>
                                                        {item.exito ? '✓ Enviado' : '✗ Error'}
                                                    </span>
                                                </div>
                                                <div className="historial-item-details">
                                                    <div><strong>Residente:</strong> {item.residente}</div>
                                                    <div><strong>Email:</strong> {item.email}</div>
                                                    <div><strong>Concepto:</strong> {item.reciboData?.concepto || 'N/A'}</div>
                                                    <div><strong>Importe:</strong> ${item.reciboData?.importe?.toFixed(2) || '0.00'}</div>
                                                    {item.enviadoEn && (
                                                        <div><strong>Enviado:</strong> {
                                                            item.enviadoEn.toDate ?
                                                                item.enviadoEn.toDate().toLocaleString('es-MX') :
                                                                new Date(item.enviadoEn.seconds * 1000).toLocaleString('es-MX')
                                                        }</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {Math.ceil(historialRecibos.length / 12) > 1 && (
                                    <div className="pagination-historial">
                                        <button
                                            onClick={() => setHistorialPage(prev => Math.max(prev - 1, 1))}
                                            disabled={historialPage === 1}
                                            className="pagination-btn"
                                        >
                                            <FaChevronLeft /> Anterior
                                        </button>
                                        <span className="pagination-info">
                                            Página {historialPage} de {Math.ceil(historialRecibos.length / 12)}
                                        </span>
                                        <button
                                            onClick={() => setHistorialPage(prev => Math.min(prev + 1, Math.ceil(historialRecibos.length / 12)))}
                                            disabled={historialPage === Math.ceil(historialRecibos.length / 12)}
                                            className="pagination-btn"
                                        >
                                            Siguiente <FaChevronLeft style={{ transform: 'rotate(180deg)' }} />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {modalMessage && (
                <div className="recibo-modal-backdrop" onClick={() => setModalMessage(null)}>
                    <div className={`recibo-modal ${modalMessage.type === 'success' ? 'recibo-modal-success' : 'recibo-modal-error'}`} onClick={(e) => e.stopPropagation()}>
                        <div className="recibo-modal-icon">
                            {modalMessage.type === 'success' ? (
                                <FaCheckCircle />
                            ) : (
                                <FaTimesCircle />
                            )}
                        </div>
                        <h3 className="recibo-modal-title">{modalMessage.title}</h3>
                        <p className="recibo-modal-message">{modalMessage.message}</p>
                        <button
                            className={`recibo-modal-btn ${modalMessage.type === 'success' ? 'recibo-modal-btn-success' : 'recibo-modal-btn-error'}`}
                            onClick={() => setModalMessage(null)}
                        >
                            Aceptar
                        </button>
                    </div>
                </div>
            )}

            {confirmModal && (
                <div className="recibo-modal-backdrop" onClick={confirmModal.onCancel}>
                    <div className="recibo-modal recibo-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="recibo-modal-icon">
                            <FaExclamationTriangle />
                        </div>
                        <h3 className="recibo-modal-title">{confirmModal.title}</h3>
                        <p className="recibo-modal-message">{confirmModal.message}</p>
                        {confirmModal.details && (
                            <p className="recibo-modal-details">{confirmModal.details}</p>
                        )}
                        <div className="recibo-modal-actions">
                            <button
                                className="recibo-modal-btn recibo-modal-btn-cancel"
                                onClick={confirmModal.onCancel}
                            >
                                Cancelar
                            </button>
                            <button
                                className="recibo-modal-btn recibo-modal-btn-confirm"
                                onClick={confirmModal.onConfirm}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal del Editor WYSIWYG */}
            {editorModalOpen && (
                <div className="editor-modal-backdrop" onClick={() => setEditorModalOpen(false)}>
                    <div className="editor-modal" onClick={e => e.stopPropagation()}>
                        <div className="editor-modal-header">
                            <h3>Redacción del Recibo</h3>
                            <button className="editor-modal-close" onClick={() => setEditorModalOpen(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="editor-modal-body">
                            <div className="editor-toolbar">
                                <div className="editor-tool-group">
                                    <select
                                        className="editor-select"
                                        value={fontFamily}
                                        onChange={(e) => {
                                            setFontFamily(e.target.value);
                                            guardarConfiguracionRecibo();
                                        }}
                                    >
                                        <option value="Arial, sans-serif">Arial</option>
                                        <option value="'Times New Roman', serif">Times New Roman</option>
                                        <option value="'Courier New', monospace">Courier New</option>
                                        <option value="Georgia, serif">Georgia</option>
                                        <option value="Verdana, sans-serif">Verdana</option>
                                    </select>
                                    <select
                                        className="editor-select"
                                        value={fontSize}
                                        onChange={(e) => {
                                            setFontSize(Number(e.target.value));
                                            guardarConfiguracionRecibo();
                                        }}
                                    >
                                        <option value="10">10pt</option>
                                        <option value="11">11pt</option>
                                        <option value="12">12pt</option>
                                        <option value="14">14pt</option>
                                        <option value="16">16pt</option>
                                        <option value="18">18pt</option>
                                    </select>
                                </div>
                                <div className="editor-tool-group">
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('bold', false, null)} title="Negrita"><FaBold /></button>
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('italic', false, null)} title="Cursiva"><FaItalic /></button>
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('underline', false, null)} title="Subrayado"><FaUnderline /></button>
                                </div>
                                <div className="editor-tool-group">
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('justifyLeft', false, null)} title="Alinear Izquierda"><FaAlignLeft /></button>
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('justifyCenter', false, null)} title="Centrar"><FaAlignCenter /></button>
                                    <button type="button" className="editor-btn" onClick={() => document.execCommand('justifyRight', false, null)} title="Alinear Derecha"><FaAlignRight /></button>
                                </div>
                            </div>

                            <div className="editor-variables-panel">
                                <span>Insertar variable:</span>
                                <div className="variables-badges">
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{nombreFracc}');
                                    }}>{'{nombreFracc}'}</span>
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{admin}');
                                    }}>{'{admin}'}</span>
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{numeroCasa}');
                                    }}>{'{numeroCasa}'}</span>
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{residente}');
                                    }}>{'{residente}'}</span>
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{direccion}');
                                    }}>{'{direccion}'}</span>
                                    <span className="variable-badge" onClick={() => {
                                        document.execCommand('insertText', false, '{adeudo}');
                                    }}>{'{adeudo}'}</span>
                                </div>
                            </div>

                            <div
                                ref={editorRef}
                                className="editor-content-area"
                                contentEditable={true}
                                suppressContentEditableWarning={true}
                                style={{
                                    fontFamily: fontFamily,
                                    fontSize: `${fontSize}pt`
                                }}
                                onBlur={(e) => {
                                    setRedaccionRecibo(e.currentTarget.innerHTML);
                                    guardarConfiguracionRecibo();
                                }}
                                dangerouslySetInnerHTML={{ __html: redaccionRecibo || DEFAULT_REDACCION }}
                            />
                        </div>
                        <div className="editor-modal-footer">
                            <button
                                className="editor-save-btn"
                                onClick={() => {
                                    if (editorRef.current) {
                                        setRedaccionRecibo(editorRef.current.innerHTML);
                                        guardarConfiguracionRecibo();
                                    }
                                    setEditorModalOpen(false);
                                }}
                            >
                                Guardar y Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenerarReciboCobro;
